import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Image as ImageIcon, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { alibabaApi, type ShareMyImageAlbumStats } from "@/api/alibaba";

export default function ShareMyImageAdmin() {
  const { data: albums = [], isLoading, error } = useQuery<ShareMyImageAlbumStats[]>({
    queryKey: ["sharemyimage-stats"],
    queryFn: async () => {
      const res = await alibabaApi.getShareMyImageStats();
      return res.data ?? [];
    },
    refetchInterval: 60_000,
  });

  const totalUsed = albums.reduce((s, a) => s + a.used, 0);
  const totalLimit = albums.reduce((s, a) => s + a.limit, 0);
  const warningCount = albums.filter(a => a.status === 'warning' || a.status === 'full').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" asChild>
          <Link to="/admin"><ArrowLeft className="h-4 w-4" /></Link>
        </Button>
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ImageIcon className="h-6 w-6" />
            ShareMyImage 相簿管理
          </h1>
          <p className="text-sm text-muted-foreground">
            監控 CDN 相簿使用狀況
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">總相簿數</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{albums.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">總用量</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {totalUsed.toLocaleString()} / {totalLimit.toLocaleString()}
            </div>
            <Progress value={totalLimit > 0 ? (totalUsed / totalLimit) * 100 : 0} className="mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">需注意</CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-3xl font-bold ${warningCount > 0 ? 'text-red-500' : 'text-green-500'}`}>
              {warningCount}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {warningCount > 0 ? '相簿接近滿或已滿' : '全部正常'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* Error */}
      {error && (
        <Card>
          <CardContent className="py-8 text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-amber-500" />
            <p className="text-muted-foreground">{(error as Error).message}</p>
          </CardContent>
        </Card>
      )}

      {/* Album list */}
      {!isLoading && !error && (
        <Card>
          <CardHeader>
            <CardTitle>相簿列表</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {albums.map((album) => {
                const pct = album.limit > 0 ? (album.used / album.limit) * 100 : 0;
                const isWarning = album.status === 'warning';
                const isFull = album.status === 'full';
                return (
                  <div
                    key={album.album_id}
                    className={`p-4 rounded-lg border ${
                      isFull ? 'border-red-500/50 bg-red-500/5' :
                      isWarning ? 'border-amber-500/50 bg-amber-500/5' :
                      'border-border'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{album.album_name}</span>
                        <Badge
                          variant={isFull ? 'destructive' : isWarning ? 'secondary' : 'outline'}
                          className={isFull ? '' : isWarning ? 'bg-amber-100 text-amber-800' : ''}
                        >
                          {isFull ? 'FULL' : isWarning ? 'WARNING' : 'OK'}
                        </Badge>
                      </div>
                      <span className="text-sm text-muted-foreground">
                        {album.used.toLocaleString()} / {album.limit.toLocaleString()}
                      </span>
                    </div>
                    <Progress
                      value={pct}
                      className={`h-2 ${isFull ? '[&>div]:bg-red-500' : isWarning ? '[&>div]:bg-amber-500' : ''}`}
                    />
                    <div className="text-xs text-muted-foreground mt-1 text-right">
                      {pct.toFixed(1)}%
                    </div>
                  </div>
                );
              })}
              {albums.length === 0 && (
                <p className="text-center text-muted-foreground py-8">尚無相簿資料</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
